/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the software.

*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Route, Switch, Redirect, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiAlert from "components/VuiAlert";

// App shell components
import Configurator from "examples/Configurator";
import TopMenuBar from "components/AppShell/TopMenuBar";
import ClientRail from "components/AppShell/ClientRail";
import PageTitle from "components/PageTitle";

// Vision UI Dashboard React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Vision UI Dashboard React routes
import routes from "routes";

// Vision UI Dashboard React contexts
import { useVisionUIController, setOpenConfigurator } from "context";
import CompanyInitializer from "components/CompanyInitializer";
import { CompanyProvider } from "./context/CompanyContext";
import { ClientProvider } from "./context/ClientContext";
import { ConfirmProvider } from "./context/ConfirmContext";
import { useAuthz, ROLES } from "./context/AuthzContext";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import AiChatWidget from "components/AiChatWidget";
import { TC } from "./tokens/text";

const TENANT_EXEMPT_ROUTES = new Set([
  "/authentication/sign-in",
  "/profile",
  "/unauthorized",
  "/visitor-portal",
]);

const ACCOUNTING_API_BASE_URL =
  process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1";

// WCAG 2.4.7 — Global focus-visible styles for keyboard accessibility
const wcagFocusStyles = {
  "*:focus-visible": {
    outline: "2px solid #1976d2 !important",
    outlineOffset: "2px !important",
    boxShadow: "0 0 0 4px rgba(25, 118, 210, 0.25) !important",
  },
  "*:focus:not(:focus-visible)": {
    outline: "none !important",
    boxShadow: "none !important",
  },
};

export default function App() {
  const [controller, dispatch] = useVisionUIController();
  const { direction, layout, openConfigurator, defaultLandingPage } = controller;
  const [clientRailCollapsed, setClientRailCollapsed] = useState(true);
  const [clientRailPinned, setClientRailPinned] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [apiHealthStatus, setApiHealthStatus] = useState({ checked: false, reachable: true });
  const { pathname } = useLocation();
  const { isLoading, isAuthenticated, hasAnyRole, hasAllPermissions, tenantId, isSuperAdmin, isVisitor, user } = useAuthz();
  const demoMode = typeof window !== 'undefined' && (window.__LEDGERHUB_DEMO || new URLSearchParams(window.location.search).get('demo') === 'true');
  const { showWarning: showIdleWarning, secondsLeft, extendSession, signOutNow } = useIdleTimeout(isAuthenticated, user?.email);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      setApiHealthStatus({ checked: false, reachable: true });
      return;
    }

    let cancelled = false;

    async function checkApiHealth() {
      try {
        const response = await fetch(`${ACCOUNTING_API_BASE_URL}/health`);
        if (!cancelled) {
          setApiHealthStatus({ checked: true, reachable: response.ok });
        }
      } catch (error) {
        if (!cancelled) {
          setApiHealthStatus({ checked: true, reachable: false });
        }
      }
    }

    checkApiHealth();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const canAccessRoute = (routeConfig) => {
    if (routeConfig.public) return true;
    // Allow demo-mode access to the main dashboard without Firebase auth
    if (demoMode && routeConfig.route === "/dashboard") return true;
    if (!isAuthenticated) return false;
    if (!hasAnyRole(routeConfig.allowedRoles)) return false;
    if (!hasAllPermissions(routeConfig.requiredPermissions)) return false;

    const needsTenant = routeConfig.requiresTenant || !TENANT_EXEMPT_ROUTES.has(routeConfig.route);
    if (needsTenant && !isSuperAdmin && !isVisitor && !tenantId) {
      // Relaxed check: allow tenant admins to access tenant-scoped routes even if tenantId is not present
      const allowedRoles = Array.isArray(routeConfig.allowedRoles) ? routeConfig.allowedRoles : [];
      if (!allowedRoles.includes(ROLES.TENANT_ADMIN)) return false;
      // If route explicitly allows tenant admins, permit access (useful for administrative pages while tokens are stale)
    }

    return true;
  };

  const dashboardShell = layout === "dashboard" && !isVisitor;
  const showClientRail = layout === "dashboard" && !isVisitor && (
    pathname === "/client-profiles" ||
    pathname.startsWith("/client-profiles/") ||
    pathname.startsWith("/individual-clients") ||
    pathname.startsWith("/directors")
  );
  const topOffset = isAuthenticated && apiHealthStatus.checked && !apiHealthStatus.reachable ? 56 : 0;
  const clientRailWidth = showClientRail ? (clientRailCollapsed ? 76 : 292) : 0;
  const contentLeftOffset = showClientRail ? clientRailWidth : 0;

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const RouteComponent = route.component;

        return (
          <Route
            exact
            path={route.route}
            key={route.key}
            render={(props) => {
              if (isLoading) return null;

              if (!route.public && !isAuthenticated) {
                return <Redirect to="/authentication/sign-in" />;
              }

              if (!canAccessRoute(route)) {
                return <Redirect to="/unauthorized" />;
              }

              return <RouteComponent {...props} />;
            }}
          />
        );
      }

      return null;
    });

  const configsButton = (
    <Box sx={{ display: { xs: "none", md: "block" } }}>
      <VuiBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="3.5rem"
        height="3.5rem"
        bgColor="info"
        shadow="sm"
        borderRadius="50%"
        position="fixed"
        right="2rem"
        bottom="2rem"
        zIndex={99}
        color="white"
        sx={{ cursor: "pointer" }}
        onClick={handleConfiguratorOpen}
      >
        <Icon fontSize="default" color="inherit">
          help
        </Icon>
      </VuiBox>
    </Box>
  );

  if (isLoading) return null;

  const apiWarningBanner =
    isAuthenticated && apiHealthStatus.checked && !apiHealthStatus.reachable ? (
      <VuiBox sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: "#b71c1c",
        borderBottom: "3px solid #c62828",
        boxShadow: "0 4px 20px rgba(183,28,28,0.5)",
        px: 3,
        py: 1.2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
      }}>
        <span style={{ fontSize: "1.1rem" }}>⚠️</span>
        <span style={{ color: TC.white, fontWeight: 700, fontSize: "0.95rem" }}>
          Backend API is not reachable — please make sure the backend server is running.
        </span>
      </VuiBox>
    ) : null;

  // Compute a safe default landing page: prefer configured defaultLandingPage when available and accessible,
  // otherwise pick the first accessible route from the routes list to avoid redirecting to removed pages.
  function flattenRoutes(allRoutes) {
    const out = [];
    for (const r of allRoutes) {
      if (r.collapse) out.push(...flattenRoutes(r.collapse));
      else if (r.route) out.push(r);
    }
    return out;
  }

  function findRouteConfigByPath(path) {
    const flat = flattenRoutes(routes);
    return flat.find(r => r.route === path);
  }

  function resolveLandingPage() {
    // 1) configured default
    const cfg = defaultLandingPage;
    const cfgRoute = findRouteConfigByPath(cfg);
    if (cfgRoute && typeof cfg === 'string' && !cfg.includes(':') && canAccessRoute(cfgRoute)) return cfg;

    // 2) first accessible explicit route (skip dynamic routes requiring params)
    const flat = flattenRoutes(routes).filter(r => typeof r.route === 'string' && !r.route.includes(':'));
    const candidate = flat.find((r) => canAccessRoute(r));
    if (candidate) return candidate.route;

    // 3) fallback
    return isSuperAdmin ? "/organisations" : isVisitor ? "/visitor-portal" : "/authentication/sign-in";
  }

  const shell = (
    <>
      {/* WCAG 2.4.1 — Skip to main content link */}
      <a
        href="#main-content"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          zIndex: 9999,
        }}
        onFocus={(e) => {
          e.target.style.position = "fixed";
          e.target.style.top = "8px";
          e.target.style.left = "8px";
          e.target.style.width = "auto";
          e.target.style.height = "auto";
          e.target.style.overflow = "visible";
          e.target.style.padding = "12px 24px";
          e.target.style.background = "#1565c0";
          e.target.style.color = "#fff";
          e.target.style.borderRadius = "8px";
          e.target.style.fontSize = "14px";
          e.target.style.fontWeight = "700";
          e.target.style.textDecoration = "none";
          e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
        }}
        onBlur={(e) => {
          e.target.style.position = "absolute";
          e.target.style.left = "-9999px";
          e.target.style.width = "1px";
          e.target.style.height = "1px";
          e.target.style.overflow = "hidden";
        }}
      >
        Skip to main content
      </a>

      {/* WCAG 2.2.1 — Idle timeout warning */}
      <Dialog
        open={showIdleWarning}
        onClose={extendSession}
        aria-labelledby="idle-timeout-title"
        aria-describedby="idle-timeout-desc"
      >
        <DialogTitle id="idle-timeout-title" sx={{ fontWeight: 700 }}>
          Session Timeout Warning
        </DialogTitle>
        <DialogContent>
          <p id="idle-timeout-desc" style={{ margin: 0 }}>
            Your session will expire in <strong>{secondsLeft}</strong> second{secondsLeft !== 1 ? "s" : ""} due to inactivity.
            Click &quot;Stay Signed In&quot; to continue working.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={signOutNow} color="inherit">Sign Out</Button>
          <Button onClick={extendSession} variant="contained" color="primary" autoFocus>
            Stay Signed In
          </Button>
        </DialogActions>
      </Dialog>

      {apiWarningBanner}
      {dashboardShell && (
        <TopMenuBar
          routes={routes}
          canAccessRoute={canAccessRoute}
          topOffset={topOffset}
          leftOffset={0}
        />
      )}
      {showClientRail && (
        <ClientRail
          topOffset={topOffset + 56}
          collapsed={clientRailCollapsed}
          pinned={clientRailPinned}
          onTogglePin={() => { setClientRailPinned((v) => !v); setClientRailCollapsed(false); }}
          onExpand={() => { if (!clientRailPinned) setClientRailCollapsed(false); }}
          onCollapse={() => { if (!clientRailPinned) setClientRailCollapsed(true); }}
        />
      )}
      {layout === "vr" && <Configurator />}
      {dashboardShell ? (
        <>
          <Box id="main-content" sx={{ pt: `${topOffset + 72}px`, maxWidth: "100vw", overflowX: "hidden" }}>
            <Box
              sx={
                showClientRail
                  ? {
                      ml: { xs: 0, md: `${contentLeftOffset}px` },
                      width: { xs: "100%", md: `calc(100% - ${contentLeftOffset}px)` },
                      maxWidth: { xs: "100%", md: `calc(100% - ${contentLeftOffset}px)` },
                      overflowX: "hidden",
                    }
                  : { width: "100%", maxWidth: "100%", overflowX: "hidden" }
              }
            >
            <Switch>
              {getRoutes(routes)}
              <Redirect from="*" to={resolveLandingPage()} />
            </Switch>
            </Box>
          </Box>
          {showClientRail && (
            <>
            </>
          )}
        </>
      ) : (
        <Switch>
          {getRoutes(routes)}
          <Redirect from="*" to={resolveLandingPage()} />
        </Switch>
      )}
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <CompanyProvider>
        <ClientProvider>
        <CompanyInitializer />
        <ThemeProvider theme={themeRTL}>
          <ConfirmProvider>
          <CssBaseline />
          <GlobalStyles styles={wcagFocusStyles} />
          {shell}
          <AiChatWidget tenantId={tenantId} />
          </ConfirmProvider>
        </ThemeProvider>
        </ClientProvider>
      </CompanyProvider>
    </CacheProvider>
  ) : (
    <CompanyProvider>
      <ClientProvider>
      <CompanyInitializer />
      <ThemeProvider theme={theme}>
        <ConfirmProvider>
        <CssBaseline />
        <PageTitle />
        {shell}
        <AiChatWidget tenantId={tenantId} />
        </ConfirmProvider>
      </ThemeProvider>
      </ClientProvider>
    </CompanyProvider>
  );
}

